import type { StrongerMemoryTypes } from '../config/environment';
import { addDays } from './calendar';
import { type ContentGraph, jsonValueOf } from './graph';
import { type SmKind, type SmMarkValue, type SmSummary, smMarkValueSchema } from './schemas';

/**
 * Leaders marking a member's exercise done (specs/v1-2-0/01-group-progress-and-marks.md,
 * sections 3 and 5). echo's backend writes each mark as the Stronger Memory group and shares
 * it with the member. One rule counts them, here, for the app and the backend alike: an
 * exercise is done on a day when the member finished it or a mark that is still done says so.
 * Per member, day and exercise, so a mark never counts twice and adds nothing to an exercise
 * the member did themselves.
 */
export interface SmMark extends SmMarkValue {
  uuid: string;
  lineageUuid: string;
}

export const SM_KINDS: readonly SmKind[] = ['read', 'write', 'math'];

/** The JSON a mark is written with: the mark, plus the attempt fields a done one reads as. */
export function markValue(mark: Omit<SmMarkValue, 'markedBy'>): Record<string, unknown> {
  return {
    kind: mark.kind,
    state: mark.state,
    source: 'other',
    markedBy: 'leader',
    startedAt: mark.changedAt,
    activeSeconds: 0,
    groupActorUuid: mark.groupActorUuid,
    date: mark.date,
    memberActorUuid: mark.memberActorUuid,
    leaderActorUuid: mark.leaderActorUuid,
    changedAt: mark.changedAt,
  };
}

/**
 * The marks among a search's roots: attempts the Stronger Memory group wrote as itself. A root
 * any other author wrote is no mark, whatever its value says, and neither is one that doesn't
 * parse. Search returns each lineage's current version.
 */
export function marksFromGraph(
  graph: ContentGraph,
  types: Pick<StrongerMemoryTypes, 'attempt'>,
  programmeGroupUuid: string,
): SmMark[] {
  const marks: SmMark[] = [];
  for (const root of graph.roots) {
    const node = graph.nodes.get(root);
    if (!node || node.contentTypeUuid !== types.attempt) continue;
    const authored = graph.actorEdges.find(
      (edge) => edge.contentUuid === node.uuid && edge.edgeType === 'AUTHORED_BY',
    );
    if (
      authored?.audience.principalType !== 'GROUP' ||
      authored.audience.uuid !== programmeGroupUuid ||
      authored.audience.scopeUuid
    ) {
      continue;
    }
    const value = smMarkValueSchema.safeParse(jsonValueOf(node));
    if (!value.success) continue;
    marks.push({ ...value.data, uuid: node.uuid, lineageUuid: node.lineageUuid });
  }
  return marks;
}

/** Marks that still say done. */
export function activeMarks(marks: readonly SmMark[]): SmMark[] {
  return marks.filter((mark) => mark.state === 'done');
}

/** Who finished an exercise on a day: the member, a leader's mark, or nobody yet. */
export type DoneBy = 'self' | 'leader' | null;

export interface MemberDay {
  date: string;
  read: DoneBy;
  write: DoneBy;
  math: DoneBy;
}

export interface MemberWeek {
  /** Monday to Sunday. */
  days: MemberDay[];
  /** Days with all three exercises done, by anyone. */
  daysDone: string[];
}

/**
 * A member's week as their group sees it (echo's backend): what their newest summary says they
 * finished themselves, plus the marks done now. A summary of another week counts for nothing. One
 * written before marks existed has no `weekDoneBySelf`; its complete days were all the member's
 * own, so they count as such.
 *
 * A member's exercises count in every group they are in, wherever they did them, but only from
 * the day they joined that group (`joinedOn`, ISO date): days before it read as nothing done.
 */
export function memberWeek(
  weekStart: string,
  summary: SmSummary | undefined,
  marks: readonly SmMark[],
  joinedOn?: string,
): MemberWeek {
  const self = new Map<string, Set<SmKind>>();
  if (summary?.weekStart === weekStart) {
    if (summary.weekDoneBySelf) {
      for (const [date, kinds] of Object.entries(summary.weekDoneBySelf)) {
        self.set(date, new Set(kinds));
      }
    } else {
      for (const date of summary.weekDaysDone) self.set(date, new Set(SM_KINDS));
    }
  }
  const marked = new Map<string, Set<SmKind>>();
  for (const mark of activeMarks(marks)) {
    const kinds = marked.get(mark.date) ?? new Set<SmKind>();
    kinds.add(mark.kind);
    marked.set(mark.date, kinds);
  }
  const days = Array.from({ length: 7 }, (_, offset): MemberDay => {
    const date = addDays(weekStart, offset);
    const by = (kind: SmKind): DoneBy =>
      joinedOn !== undefined && date < joinedOn
        ? null
        : self.get(date)?.has(kind)
          ? 'self'
          : marked.get(date)?.has(kind)
            ? 'leader'
            : null;
    return { date, read: by('read'), write: by('write'), math: by('math') };
  });
  return {
    days,
    daysDone: days.filter((day) => day.read && day.write && day.math).map((day) => day.date),
  };
}
